import { next, schedule, scheduleOnce } from '@ember/runloop';

import $ from 'jquery';
import tinymce from 'tinymce';

import { StoryElementType } from 'webapp/enums/enums';

import type DataStoryBase from 'webapp/components/data-story/base';

import type StoryElementBase from 'webapp/models-custom/story-element/base';

export default class StoryElement extends HTMLElement {
	private component: DataStoryBase<unknown> | null = null;
	model: StoryElementBase | null = null;
	isDisconnected = false;

	private element: JQuery<HTMLElement>;
	private tooltipActive = false;
	private clickableTypes = [StoryElementType.VALUE, StoryElementType.COMPARE_VALUE];

	constructor() {
		super();

		this.addEventListener('click', (event: PointerEvent) => {
			if (this.model?.isEditable) {
				this.model.openEdit(this);
			} else if (this.isClickable && this.model) {
				const point =
					this.model.type === StoryElementType.VALUE
						? this.model.dataPoint
						: this.model.comparePoint;

				if (point?.onClick) {
					const target = this.element[0] as EventTarget;
					point.onClick({ ...event, target });
				}
			}
		});

		this.element = $(this);
		// tooltip will be set up after the element is connected to the DOM
	}

	get isClickable() {
		return this.model && this.clickableTypes.includes(this.model.type as StoryElementType);
	}

	get isModelEmpty() {
		return !!this.model?.isEmpty;
	}

	get isModelMissingOrDeleted() {
		return !this.model || this.model.isMissingOrDeleted;
	}

	get isLoading(): boolean {
		return !!this.model?.isLoading;
	}

	private setupTooltip(): void {
		this.element.tooltipster({
			contentAsHTML: true,
			theme: 'tooltipster-default',
			delay: 500,
			maxWidth: 200,
			position: 'top',
			trigger: 'hover',
			animation: 'fade',
			animationDuration: 200,
			content: () => {
				return this.getAttribute('data-title') || '';
			},
			functionBefore: (_instance: unknown, helper: { origin: HTMLElement }) => {
				const helperEl = $(helper.origin);
				if (helperEl.is(':hidden')) {
					return false;
				}
				return true;
			},
		});

		this.tooltipActive = true;
	}

	private destroyTooltip(): void {
		if (this.tooltipActive && this.element.tooltipster) {
			try {
				this.element.tooltipster('destroy');
			} catch (_e) {
				// nothing
			}
			this.tooltipActive = false;
		}
	}

	setModel(isReconnected = false) {
		if (this.isDisconnected) {
			return;
		}

		let component: DataStoryBase<unknown> | null = null;

		const componentNode = this.closest('.story-container');
		if (componentNode) {
			const getComponentFn = $(componentNode).data('getComponent') as
				(() => DataStoryBase<unknown>) | undefined;
			if (getComponentFn) {
				component = getComponentFn() || null;
				if (component) {
					const order = +(this.getAttribute('data-order') || ''),
						type = this.getAttribute('data-type')!;

					const model = component.createStoryElementFor(order, type);
					this.model = model;
					if (isReconnected) {
						next(() => {
							// next, to run it after onElementRemove which happens on disconnect
							component?.onElementAdd(this);
						});
					} else {
						component.onElementAdd(this);
					}
				}
			}
		}

		this.component = component;
	}

	removeModel() {
		const component = this.component;
		if (component && this) {
			component.onElementRemove(this);
		}
	}

	updateAttributes() {
		// console.log('call updateAttributes', { type: this.getAttribute('data-type'), order: this.getAttribute('data-order') });

		if (this.isDisconnected) {
			// can happen since we're calling this in afterRender
			return;
		}

		const colorClass = `color-${this.model?.color}`;
		if (!this.classList.contains(colorClass)) {
			// change of color
			this.classList.forEach((c) => {
				if (c.startsWith('color-')) {
					this.classList.remove(c);
				}
			});
			this.classList.add(colorClass);
		}

		this.setAttribute('data-title', this.model?.shortTitle || '');

		const isEditable = this.model?.isEditable;
		if (isEditable) {
			this.classList.add('editable');
		}

		if (this.isClickable) {
			this.classList.add('text-clickable');
		}

		let eleNode: HTMLElement | null = this.querySelector('ele');
		if (!eleNode) {
			eleNode = document.createElement('ele');
			this.append(eleNode);
		}

		if (isEditable) {
			let deleteNode: HTMLElement | null = this.querySelector('a.delete');
			if (!deleteNode) {
				deleteNode = this.createDeleteNode();
				this.append(deleteNode);
			}
		}

		const isLoading = this.isLoading && !this.isModelEmpty && !this.isModelMissingOrDeleted;
		this.classList.toggle('loading', isLoading);

		if (isLoading) {
			eleNode.textContent = '';
		} else {
			if (this.isModelMissingOrDeleted) {
				eleNode.textContent = '<unknown>';
			} else if (this.isModelEmpty) {
				eleNode.textContent = '-';
			} else if (this.model) {
				eleNode.textContent = '';
				this.model.fillElementContent(eleNode);
			}
		}

		if (isEditable) {
			eleNode.append();
		}
	}

	scheduleUpdateAttributes(): void {
		// legacy observers trigger in sync mode by default
		scheduleOnce('afterRender', this, this.updateAttributes);
	}

	createDeleteNode(): HTMLElement {
		const deleteNode = document.createElement('a');
		deleteNode.classList.add('delete');

		const iconNode = document.createElement('ui-icon');
		iconNode.setAttribute('name', 'Plus');
		iconNode.setAttribute('size', 'xs');
		deleteNode.appendChild(iconNode);

		deleteNode.addEventListener('click', (e) => {
			e.stopPropagation();

			// after we delete this node, we must inform tinymce instance
			const editorNode = this.closest('.mce-content-body');

			this.remove();

			if (editorNode) {
				const editor = tinymce.get(editorNode.id);
				if (editor) {
					editor.fire('change');
				}
			}
		});
		return deleteNode;
	}

	connectedCallback() {
		// reconnect happens when we apply html (e.g. bold) around the custom story element
		const isReconnected = this.isDisconnected;
		this.isDisconnected = false;

		this.classList.add('mceNonEditable');
		this.setAttribute('contenteditable', 'false');

		// afterRender so getComponent method can bind to the container node
		schedule('afterRender', () => {
			this.setModel(isReconnected);
			this.updateAttributes();
			this.setupTooltip();
		});
	}

	attributeChangedCallback(_name: string, _oldValue: string, _newValue: string) {
		this.updateAttributes();
	}

	disconnectedCallback() {
		this.isDisconnected = true; // flag so we don't go update anything anymore
		this.removeModel();
		this.destroyTooltip();
	}
}
